import { Modal, Button, Table, InputGroup, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Select from "react-select";
import { Popover } from "react-tiny-popover";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import currency from "currency.js";
import DateTime from "react-datetime";
import moment from "moment";

import { CustomInput } from "../utils/CustomInput";
import { useAuth } from "../../hooks/useAuth";
import "../../assets/scss/scoped/issueItem.scss";
import ConfirmDialog from "../ConfirmDialogue";
import { fetchActionsUtil } from "../../utils/helpers";
import GetTemplateModal from "./GetTemplateModal";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { DeleteIcon } from "../Icons";
import ModalLoader from "../utils/ModalLoader";
import NewRequisitionModal from "../Requisition/NewRequisition";

export const CreateProductionPlanModal = (props) => {
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [activeTableIndex, setActiveTableIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [selectedReceiver, setSelectedReceiver] = useState("");
  const [result, setResult] = useState();
  const [itemsRequested, setItemsRequested] = useState([]);
  const [showNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { backendUrl, token, user } = useAuth();

  const initialValues = {
    Bar_Code: "",
    Item_Name: "",
    Item_Desc: "",
    Product_Name: "",
    Cat_Name: "",
    RequesterName: "",
    Requester: "",
    UnitCost: "",
    Date_Log: moment(new Date()),
    Quantity: "",
    UserName: "",
    plant: "",
    Plan_Name: "",
    Plan_Id: Date.now(),
    complete: true,
  };

  const handleInputChange = ({ index, name, value }) => {
    const oldTableData = tableData;
    oldTableData[index][name] = value;
    setTableData([...oldTableData]);
  };

  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);

  const plantLocal = JSON.parse(window.localStorage.getItem("plantDetails"));

  const fetchItems = async () => {
    const data = await fetchActionsUtil(
      `${backendUrl}/api/production/IssueTo_Officers`,
      "GET",
      token
    );
    data.staffs = data.staffs.map((el) => ({
      value: el.Staff_ID,
      label: el.Name,
    }));

    data.warehouse = [{ value: "Sales", label: "Sales" }].concat(
      ...data.warehouse.map((el) => ({
        value: el.W_ID,
        label: el.W_name,
      }))
    );

    return data;
  };

  const { data, isLoading } = useQuery(["GET_OTHERS"], () => fetchItems(), {
    enabled: true,
  });

  const sendProductLog = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/production/product-plan`,
        "POST",
        token,
        payload
      ),
    {
      onSuccess: ({ message, data }) => {
        toast.success("Successfully Created");

        formik.resetForm({
          values: {
            ...initialValues,
          },
        });

        setTableData([]);
        props.refetchMain();
        props.onHide();
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const editProductPlan = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/production/product-plan`,
        "PUT",
        token,
        payload
      ),
    {
      onSuccess: ({ message, data }) => {
        toast.success("Successfully Updated");

        props.onHide();
        props.refetchMain();
      },
      onError: () => {
        toast.error(`Unable to perform action`);
      },
    }
  );

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object().shape({
      Bar_Code: yup.string().required("Bar Code is required"),
      Item_Name: yup.string().required("Item Name is required"),
      Quantity: yup.string().required("Quantity is required"),
      Product_Name: yup.string().required("Product is required"),
      Cat_Name: yup.string().required("Category by is required"),
    }),
    onSubmit: async (values) => {
      if (values.Plan_Name === "") {
        return toast.error("Enter plan name");
      }

      if (values.Quantity === "") {
        return toast.error("Quantity is needed");
      }

      if (tableData.length < 1) {
        return toast.error("Add Items");
      }

      if (props?.choosedItem) {
        if (
          await ConfirmDialog({
            title: "Edit Production Plan",
            description: "Are you sure you want to edit this production plan",
          })
        ) {
          // console.log(tableData);
          editProductPlan.mutate({ tableData });
        }
      } else {
        if (
          await ConfirmDialog({
            title: "Save Production Plan",
            description: "Are you sure you want to save this production plan",
          })
        ) {
          // console.log(tableData);
          sendProductLog.mutate({ tableData });
        }
      }
    },
  });

  useEffect(() => {
    let active = true;
    if (selectedReceiver === "Sales") {
      setResult(data?.staffs);
    } else {
      setIsLoadingAddToCart(true);
      load();
      async function load() {
        setResult([]); // this is optional
        const { warehouseUsers } = await fetchActionsUtil(
          `${backendUrl}/api/warehouse/warehouse-users/${selectedReceiver}`,
          "GET"
        );

        if (!active) {
          return;
        }
        setResult(() =>
          warehouseUsers?.map((el) => ({
            value: el.Staff_ID,
            label: el.Name,
          }))
        );
        setIsLoadingAddToCart(false);
      }
    }
    return () => {
      active = false;
    };
  }, [selectedReceiver, data?.staffs, backendUrl]);

  const handleSelectedItem = (el) => {
    // console.log(el);
    formik.setFieldValue("Bar_Code", el.Bar_Code);
    formik.setFieldValue("Item_Name", el.Item_Name);
    formik.setFieldValue("Product_Name", el.Product_Name);
    formik.setFieldValue("Cat_Name", el.Cat_Name);
    formik.setFieldValue("UnitCost", el.UnitCost);
    formik.setFieldValue("UserName", user.username);
    formik.setFieldValue("plant", plantLocal.PLID);
    formik.setFieldValue("Quantity", "");
    if (props?.choosedItem) {
      addToTable(el, props?.choosedItem?.Quantity);
      el.plant = props?.choosedItem?.plant;
      el.Plan_Name = props?.choosedItem?.Plan_Name;
      el.Plan_Id = props?.choosedItem?.Plan_Id;
    }
    setSelectedItem(el);
    setTableData([]);
    setShowItemsPopover(false);
    setErrorMessage(null);
    setItemsRequested([]);
  };

  useEffect(() => {
    if (props?.choosedItem) {
      formik.setFieldValue("Bar_Code", props?.choosedItem?.Bar_Code);
      formik.setFieldValue("Item_Name", props?.choosedItem?.Item_Name);
      formik.setFieldValue("Item_Desc", props?.choosedItem?.Item_Desc);
      formik.setFieldValue("Product_Name", props?.choosedItem?.Product_Name);
      formik.setFieldValue("Cat_Name", props?.choosedItem?.Cat_Name);
      formik.setFieldValue("Product_Model", props?.choosedItem?.Product_Model);
      formik.setFieldValue(
        "UnitCost",
        parseFloat(props?.choosedItem?.UnitCost)
      );
      formik.setFieldValue("Plan_Name", props?.choosedItem?.Plan_Name);
      formik.setFieldValue("Plan_Id", props?.choosedItem?.Plan_Id);
      formik.setFieldValue("complete", props?.choosedItem?.complete);
      formik.setFieldValue("Quantity", props?.choosedItem?.Quantity);
      formik.setFieldValue("plant", props?.choosedItem?.plant);
      formik.setFieldValue(
        "Date_Log",
        moment(new Date(props?.choosedItem?.dueDate))
      );

      setTableData([props?.choosedItem]);
    }
  }, [props?.choosedItem]);

  const onDelete = (barcode) => {
    const item = tableData.filter((d) => d.Bar_Code !== barcode);
    setTableData(item);
  };

  const addToTable = async (el, qty) => {
    if (el.Bar_Code === "") {
      return toast.error("Enter Item Id");
    }
    if (qty === "") {
      return toast.error("Quantity is needed");
    }

    setIsLoadingAddToCart(true);
    const newData = { ...selectedItem, ...el, Quantity: qty };

    try {
      const { message } = await fetchActionsUtil(
        `${backendUrl}/api/production/check-quantity-items/${plantLocal.PLID}`,
        "POST",
        token,
        newData
      );

      setIsLoadingAddToCart(false);
      if (message.length > 0) {
        setItemsRequested(message?.map((er) => er.item));
        setErrorMessage("Item Required not enough");
        // message?.map((er) => toast.error(er.message));
      } else {
        setErrorMessage(null);
        setItemsRequested([]);
      }
    } catch (error) {
      console.log(error);

      setIsLoadingAddToCart(false);
      // return;
    }
    // setErrorMessage(null);
    setTableData([newData]);
  };

  const onKeyPressAction = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      //Enter keycode
      addToTable(formik.values, e.target.value);
    }
  };

  return (
    <Modal
      onHide={props.onHide}
      show={props.show}
      backdropClassName={`global-backdrop`}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="my-style-modal"
      animation={false}
      enforceFocus={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h1>{`${props?.choosedItem ? "Edit" : "Add"}`} Production Plan</h1>
          </div>

          <p>
            {`${props?.choosedItem ? "Edit" : "Add"}`} production plan by
            filling in the following forms.
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className="container">
            <div>
              <div className="form-relative">
                {/*  */}
                <div className="row">
                  <div className="col-8">
                    <CustomInput
                      name="Plan_Name"
                      placeholder="Enter Plan Name"
                      typeOfInput="text"
                      label="Tittle"
                      value={formik.values.Plan_Name}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <label htmlFor="date" className="form-label">
                      Item Code
                    </label>
                    <InputGroup>
                      <Form.Control
                        name="Bar_Code"
                        placeholder="Enter Item Code"
                        value={formik.values.Bar_Code}
                      />

                      <Popover
                        isOpen={showItemsPopover}
                        onClickOutside={() => setShowItemsPopover(false)}
                        content={() => (
                          <GetTemplateModal
                            handleSelectedItem={handleSelectedItem}
                          />
                        )}
                        position="bottom"
                      >
                        <InputGroup.Text
                          onClick={() => setShowItemsPopover(!showItemsPopover)}
                        >
                          <MagnifyIcon />
                        </InputGroup.Text>
                      </Popover>
                    </InputGroup>

                    {formik.touched.Bar_Code && !!formik.errors.Bar_Code ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Bar_Code}
                      </span>
                    ) : null}
                  </div>
                </div>
                {/*  */}

                <div className="row">
                  <div className="col-4 mb-3">
                    <CustomInput
                      name="Item_Name"
                      placeholder="Enter Item Name"
                      typeOfInput="text"
                      label="Item Name"
                      disabled
                      value={formik.values.Item_Name}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <div className="col-4 mb-3">
                    <CustomInput
                      name="Product_Name"
                      placeholder="Enter Item Name"
                      typeOfInput="text"
                      label="Product"
                      disabled
                      value={formik.values.Product_Name}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <CustomInput
                      name="Cat_Name"
                      placeholder="Enter Item Name"
                      typeOfInput="text"
                      label="Category"
                      disabled
                      value={formik.values.Cat_Name}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
                {/*  */}

                <div className="row">
                  <div className="col-4 mb-3">
                    <CustomInput
                      name="Quantity"
                      placeholder="Enter Quantity"
                      typeOfInput="text"
                      label="Quantity"
                      disabled={!formik.values.Bar_Code}
                      value={formik.values.Quantity}
                      onBlur={(e) => {
                        addToTable(formik.values, e.target.value);
                      }}
                      onKeyPress={onKeyPressAction}
                      onChange={(e) => {
                        formik.setFieldValue("Quantity", e.target.value, true);
                      }}
                    />
                    {formik.touched.Quantity && !!formik.errors.Quantity ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Quantity}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-4 mb-3">
                    <CustomInput
                      name="UnitCost"
                      placeholder="Enter Item Name"
                      typeOfInput="text"
                      label="Cost Price"
                      value={formik.values.UnitCost}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.UnitCost && !formik.errors.UnitCost ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.UnitCost}
                      </span>
                    ) : null}
                  </div>
                  <div className="col-4">
                    <label htmlFor="date" className="form-label">
                      Due Date
                    </label>
                    <DateTime
                      timeFormat={false}
                      closeOnSelect={true}
                      closeOnClickOutside={true}
                      dateFormat="MMM DD, YYYY"
                      inputProps={{
                        className: `date-input form-control`,
                        placeholder: "Select date",
                        readOnly: true,
                      }}
                      value={formik.values.Date_Log}
                      onChange={(date) => {
                        formik.setFieldValue("Date_Log", date, true);
                      }}
                    />
                  </div>
                </div>

                {/*  */}
                {/* Table */}

                <div className="mt-5 mb-4">
                  <div className="mb-3 text-primary">
                    Items required to fulfill the plan
                  </div>

                  <Table
                    responsive
                    borderless
                    striped
                    className="product-table"
                  >
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Bar Code</th>
                        <th>Name</th>
                        <th>Quantity</th>
                        {/* <th>Unit Cost</th>
                        <th>Sub Total</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData?.length > 0 &&
                        tableData[0]?.prod_profiles?.map((el, index) => (
                          <tr
                            key={index}
                            className={`${activeTableIndex === index &&
                              "active-row"}`}
                            onClick={() =>
                              activeTableIndex !== index &&
                              setActiveTableIndex(index)
                            }
                          >
                            <td>{index + 1}</td>
                            <td>{el.item}</td>
                            <td>{el.title}</td>
                            <td>{el.quantity}</td>
                            {/* <td>
                              <input
                                className="border-0 text-center"
                                type="number"
                                value={el.Quantity}
                                disabled
                                onKeyDown={(e) =>
                                  e.key === "Enter" && e.target.blur()
                                }
                                name="Quantity"
                                onChange={(e) =>
                                  handleInputChange({
                                    index,
                                    name: e.target.name,
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>{el?.UnitCost}</td>
                            <td className="d-flex align-items-center">
                              <span>
                                {currency(
                                  parseFloat(el?.UnitCost) *
                                    parseInt(el?.Quantity),
                                  {
                                    symbol: "",
                                  }
                                ).format()}
                              </span>
                              <span
                                className="mx-3 p-cursor"
                                onClick={() => onDelete(el.Bar_Code)}
                              >
                                <DeleteIcon />
                              </span>
                            </td> */}
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>

                {/*  */}

                {errorMessage && (
                  <div className="d-flex justify-content-center">
                    <p className="text-danger">
                      <b>{errorMessage}</b>
                    </p>
                  </div>
                )}
                <div className="d-flex justify-content-end mt-3">
                  {props?.choosedItem ? (
                    <button
                      onClick={formik.handleSubmit}
                      className="btn btn-primary"
                      disabled={isLoadingAddToCart || editProductPlan.isLoading}
                    >
                      {isLoadingAddToCart
                        ? "Checking..."
                        : editProductPlan.isLoading
                        ? "Sending..."
                        : "Edit Plan"}
                    </button>
                  ) : (
                    <button
                      onClick={formik.handleSubmit}
                      className="btn btn-primary"
                      disabled={isLoadingAddToCart || sendProductLog.isLoading}
                    >
                      {isLoadingAddToCart
                        ? "Checking..."
                        : sendProductLog.isLoading
                        ? "Sending..."
                        : "Create Plan"}
                    </button>
                  )}

                  {itemsRequested.length > 0 && (
                    <button
                      className="btn btn-primary mx-3"
                      onClick={() => setShowNewRequisitionModal(true)}
                    >
                      Request for items
                    </button>
                  )}
                </div>
              </div>
            </div>
            <ModalLoader
              show={
                sendProductLog.isLoading ||
                isLoading ||
                isLoadingAddToCart ||
                editProductPlan.isLoading
              }
            />

            {showNewRequisitionModal && (
              <NewRequisitionModal
                modalTitle="Send for Approval"
                defaultFormValues={{
                  title:
                    formik.values.Plan_Name !== ""
                      ? formik.values.Plan_Name
                      : "",
                  description: "",
                  type: "Material Request",
                }}
                itemsRequested={itemsRequested}
                setShowNewRequisitionModal={setShowNewRequisitionModal}
                /* handleCreatedRequisition={handleCreatedRequisition} */
              />
            )}

            {/*  */}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
